// ADR-0142 — el cierre por falta de respuesta en la vista del proyecto.
//
// Dos piezas, ambas sólo cuando el servidor dice `enabled` (receta con
// el reloj + job activo en este entorno); con `enabled: false` no se
// pinta nada:
//   - `CierreKpis`: el conteo APARTE de las cerradas por falta de
//     respuesta frente a las cerradas con conformidad. Un cierre
//     automático no es una conformidad y no se suma a esa métrica.
//   - `PropietariosSinContacto`: las observaciones que no pueden cerrarse
//     porque al propietario no le llegan los correos (sin propietario,
//     cuenta sin activar, envío fallido). El jefe lo contacta por otra
//     vía; mientras el aviso no salga, la observación no se cierra.
//
// Patrones del panel postventa: `KpiTile` denso, y la tabla
// `dtable--rowlink` de la bandeja de consultas con el título como enlace.
import { useState } from "react";
import { Link } from "react-router-dom";
import type { PostventaClosureSummaryResponse } from "@bitacorapro/api-types";
import { Icon } from "./icons";
import { KpiTile } from "./KpiTile";
import { Pager } from "./Pager";
import { avisoBloqueado, intentosLabel, MOTIVO_BLOQUEO } from "../lib/postventa/closure";
import { fmtDate } from "../lib/util";

const POR_PAGINA = 10;

/** Las dos tiles del conteo por motivo de cierre. `archivoHref`: el
 *  archivo del proyecto; la tile de las cerradas sin respuesta abre su
 *  filtro (el kind `closed_no_response` es exactamente ese motivo). */
export function CierreKpis({
  summary,
  archivoHref,
}: {
  summary: PostventaClosureSummaryResponse | undefined;
  archivoHref: string;
}) {
  if (!summary?.enabled) return null;
  return (
    <>
      <KpiTile
        dense
        label="Cerradas con conformidad"
        value={summary.closedByConformity}
        hint="El propietario confirmó el arreglo"
      />
      <KpiTile
        dense
        label="Cerradas por falta de respuesta"
        value={summary.closedNoResponse}
        hint="El propietario no respondió a tiempo"
        {...(summary.closedNoResponse > 0
          ? { to: `${archivoHref}?estado=closed_no_response` }
          : {})}
      />
    </>
  );
}

/** La lista de observaciones bloqueadas porque al propietario no le
 *  llegan los avisos. Sin bloqueadas, no hay panel. */
export function PropietariosSinContacto({
  summary,
  observacionHref,
}: {
  summary: PostventaClosureSummaryResponse | undefined;
  observacionHref: (findingId: string) => string;
}) {
  const [pagina, setPagina] = useState(1);
  if (!summary?.enabled || summary.blocked.length === 0) return null;
  const { blocked } = summary;
  const paginas = Math.max(1, Math.ceil(blocked.length / POR_PAGINA));
  const actual = Math.min(pagina, paginas);
  const filas = blocked.slice((actual - 1) * POR_PAGINA, actual * POR_PAGINA);

  return (
    <section className="panel" style={{ marginBottom: 20 }} aria-labelledby="sin-contacto">
      <div className="panel__head">
        <div className="panel__title" id="sin-contacto">
          Propietarios sin contacto
        </div>
        <div className="panel__sub mono">{blocked.length}</div>
      </div>
      <div className="panel__body--flush">
        <table className="dtable dtable--compact dtable--rowlink dtable--no-check">
          <caption className="sr-only">
            Observaciones que no pueden cerrarse porque al propietario no le llegan los avisos
          </caption>
          <thead>
            <tr>
              <th scope="col">Unidad</th>
              <th scope="col" className="col-title">
                Observación
              </th>
              <th scope="col">Motivo</th>
              <th scope="col">Aviso pendiente</th>
              <th scope="col">Último intento</th>
              <th scope="col" className="col-chev">
                <span className="sr-only">Abrir</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {filas.map((b) => (
              <tr key={`${b.findingId}:${b.notice}:${b.reminderNumber ?? 0}`}>
                <td className="cell-strong">{b.assetUnit ?? b.assetLabel}</td>
                <td className="col-title">
                  <div className="cell-with-sub">
                    <Link
                      to={observacionHref(b.findingId)}
                      className="cell-strong cell-strong--link row-title"
                    >
                      {b.observationLabel}
                    </Link>
                    {b.folio && <span className="cell-sub mono">{b.folio}</span>}
                  </div>
                </td>
                <td className="cell-meta">{MOTIVO_BLOQUEO[b.reason]}</td>
                <td>
                  <div className="cell-with-sub">
                    <span>{avisoBloqueado(b)}</span>
                    {b.attempts > 0 && (
                      <span className="cell-sub">{intentosLabel(b.attempts)}</span>
                    )}
                  </div>
                </td>
                <td className="mono">{fmtDate(b.lastAttemptAt)}</td>
                <td className="cell-meta col-chev" aria-hidden="true">
                  <Icon.ChevronRight s={16} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {paginas > 1 && (
        <Pager
          currentPage={actual}
          totalPages={paginas}
          totalItems={blocked.length}
          pageSize={POR_PAGINA}
          onPage={setPagina}
        />
      )}
      <div className="panel__foot">
        A estos propietarios no les llegan los avisos. Contáctalos por otra vía: mientras un
        recordatorio no salga, la observación no se cierra sola.
      </div>
    </section>
  );
}
